/* eslint-disable */
import axios from 'axios';
import { get } from 'lodash';
import {
  QueryAndSortDto,
  AuditLogSummaryDto,
  FilterItemDto,
  ContentDto,
  OptionDto,
  OrganisationDto,
  OrganisationDtoQueryResultDto,
  MarkNotificationRequestDto,
  KosmosReportDto,
  OrganisationScoredCompaniesDto,
  CompanyDetailedDtoQueryResultDto,
  OrganisationDetailDto,
  TriggerScoringByNameDto,
  UpdateOrganisationRequestDto,
} from 'models/dtoTypes';
import { API_URL } from 'projectConfig';
import {
  getAuditLogApi,
  getCommodityCodesApi,
  getCompanyApi,
  getContentApi,
  getKosmosCompanyApi,
  getNotificationApi,
  getOrganisationScoredCompaniesApi,
  getOrganisationsApi,
  getQuestionnaireApi,
  getSectorCodesApi,
} from 'services';

export interface ErrorHandler {
  (e: any): void;
}

export function forceCast<T>(input: any): T {
  // @ts-ignore <-- forces TS compiler to compile this as-is
  return input;
}

export const openapiJson = `${API_URL}/swagger/v1/swagger.json`;

export const processResponse = <T>(result: any): T => {
  if (result.status >= 200 && result.status < 300) {
    return result.data as T;
  } else {
    console.log(result.response);
    // Handle different error scenarios
    if (axios.isAxiosError(result) && result.response) {
      // console.log(e.response?.data);
      const errorMessage =
        get(result.response?.data, 'message') ||
        get(result.response?.data, 'title') ||
        'Something went wrong';
      throw new Error(errorMessage);
    } else {
      // native error
      console.error(result);
      throw new Error('Something went wrong');
    }
  }
};

export const handleError = (e: any, errorHandler?: ErrorHandler) => {
  const errorResponse = e.response;
  const statusCode = errorResponse?.status;
  if (!errorResponse) {
    errorHandler &&
      errorHandler({
        type: 'connection_refused',
        message: 'Cannot connect to server',
      });
  } else if (statusCode === 400) {
    errorHandler && errorHandler(errorResponse);
  } else if (statusCode === 401) {
    // should not happen, only when auto renew failed or timeout, redirect to landing page for manually login
    window.location.href = window.location.origin + '/401';
    // This will result multiple login_in_progress error
  } else if (statusCode === 404) {
    window.location.href = '/404';
  } else if (statusCode === 500) {
    window.location.href = '/500';
  }
};

export const GetSection = async (alias: string) => {
  try {
    const client = await getQuestionnaireApi();
    const response = await client.getSection(alias);
    return response;
  } catch (e) {
    handleError(e);
  }
};

export const DeleteSubSectionResponse = async (queryParams: {
  sectionAlias: string;
  subSectionResponseId: string;
  alias: string;
}) => {
  try {
    const client = await getQuestionnaireApi();
    const { sectionAlias, subSectionResponseId, alias } = queryParams;
    const response = await client.deleteSubSectionResponse(
      sectionAlias,
      subSectionResponseId,
      alias,
    );
    return response;
  } catch (e) {
    handleError(e);
  }
};

export const DeleteFile = async (queryParams: { sectionAlias: string; fileResponseId: string }) => {
  try {
    const client = await getQuestionnaireApi();
    const { sectionAlias, fileResponseId } = queryParams;

    const response = await client.deleteFile(sectionAlias, fileResponseId);
    return response;
  } catch (e) {
    handleError(e);
  }
};

export const GetFilePublicUrl = async (filePrivateUrl?: string) => {
  try {
    const client = await getQuestionnaireApi();
    const response = await client.getFilePublicUrl(filePrivateUrl);
    return response;
  } catch (e) {
    handleError(e);
  }
};

interface SearchParams {
  query?: string;
  ids?: string[];
  hsDigitLength?: any;
  ebopsCodesSuppressed?: any;
}

export const SearchHsCode = async (searchParams: SearchParams) => {
  const client = await getCommodityCodesApi();
  const { query = '', ids = [], hsDigitLength = null, ebopsCodesSuppressed = null } = searchParams;
  const response = await client.searchCommodity(query, ids, hsDigitLength, ebopsCodesSuppressed);
  return response;
};

export const SearchSectorCode = async (searchParams: SearchParams) => {
  const client = await getSectorCodesApi();
  const { query, ids } = searchParams;

  const response = await client.searchSector(query, ids);
  return response;
};

export const CompanySearchTradesunAI = async (queryParams: {
  companyName: string;
  companyNumber: string;
  countryCode: string;
  addressLine: string;
  postalCode: string;
}) => {
  const client = await getKosmosCompanyApi();
  const { companyName, countryCode, addressLine, postalCode, companyNumber } = queryParams;
  try {
    const response = await client.searchByTradesunAI(
      countryCode,
      companyName,
      companyNumber,
      addressLine,
      postalCode,
    );
    return processResponse<CompanyDetailedDtoQueryResultDto>(response);
  } catch (e) {
    console.log(e);
    return processResponse<CompanyDetailedDtoQueryResultDto>(e);
  }
};

export const CompanySearchV2 = async (queryParams: {
  companyName: string;
  companyNumber: string;
  countryCode: string;
  addressLine: string;
  postalCode: string;
}) => {
  const client = await getKosmosCompanyApi();
  const { companyName, countryCode, addressLine, postalCode, companyNumber } = queryParams;
  try {
    const response = await client.searchV2(
      countryCode,
      companyName,
      companyNumber,
      addressLine,
      postalCode,
    );
    return processResponse<CompanyDetailedDtoQueryResultDto>(response);
  } catch (e) {
    console.log(e);
    return processResponse<CompanyDetailedDtoQueryResultDto>(e);
  }
};

export const CompanySearchByIds = async ({ ids }: { ids: string[] }) => {
  const client = await getKosmosCompanyApi();
  const { data } = await client.companySearchByIds(ids);
  return data;
};

export const CompanySearchByAddress = async ({
  query,
  countryCode3,
  addressRegion,
  streetAddressLine1,
  addressLocality,
  postalCode,
  pageIndex,
  pageSize,
  order,
}: {
  query: string;
  streetAddressLine1: string;
  countryCode3?: string;
  addressRegion?: string;
  addressLocality?: string;
  postalCode?: string;
  pageIndex?: number;
  pageSize?: number;
  order?: string;
}) => {
  const client = await getKosmosCompanyApi();
  const { data } = await client.searchByAddress(
    query,
    countryCode3,
    addressRegion,
    streetAddressLine1,
    addressLocality,
    postalCode,
    pageIndex,
    pageSize,
    order,
  );
  return data;
};

export const CompanySearchByGeo = async ({
  query,
  countryCode3,
  radiusLat,
  radiusLon,
  radiusPostalCode,
  radiusDistance,
  radiusUnit,
  pageIndex,
  pageSize,
  order,
}: {
  query: string;
  radiusLat: number;
  radiusLon: number;
  radiusDistance: number;
  radiusPostalCode?: string;
  radiusUnit: string;
  countryCode3?: string;
  pageIndex?: number;
  pageSize?: number;
  order?: string;
}) => {
  const client = await getKosmosCompanyApi();
  const { data } = await client.searchByGeolocation(
    query,
    countryCode3,
    radiusLat,
    radiusLon,
    radiusPostalCode,
    radiusDistance,
    radiusUnit,
    pageIndex,
    pageSize,
    order,
  );
  return data;
};

export const GetOptionSet = async (alias: string, query?: string) => {
  try {
    const client = await getQuestionnaireApi();
    const { data } = await client.getOptions(alias, query);
    return data as OptionDto[];
  } catch (e) {
    handleError(e);
    return [] as OptionDto[];
  }
};

export const GetContent = async () => {
  try {
    const client = getContentApi();
    const response = await client.getContent('ui');
    return response.data;
  } catch (e) {
    handleError(e);
    return [] as ContentDto[];
  }
};

export const GetShortCode = async (shortCode: string) => {
  const client = getContentApi();
  const response = await client.decodeShortCode(shortCode);
  console.log(response);
  return response.data;
};

export const DisconnectUser = async (companyId: string) => {
  const client = await getCompanyApi();
  const response = await client.disconnectUser(companyId);
  return response;
};

export const GetNotifications = async () => {
  try {
    const client = await getNotificationApi();
    const res = await client.getNotifications();
    return res.data;
  } catch (e) {
    handleError(e);
  }
};

export const MarkNotification = async (request: MarkNotificationRequestDto) => {
  try {
    const client = await getNotificationApi();
    const response = await client.markNotification(request);
    return response.status === 200;
  } catch (e) {
    handleError(e);
    return false;
  }
};

export const MarkAllNotification = async () => {
  try {
    const client = await getNotificationApi();
    const response = await client.markAllNotification();
    return response.status === 200;
  } catch (e) {
    handleError(e);
    return false;
  }
};

// trigger a long process update score
export const UpdateScoring = async ({ id, notifyUser }: { id: string; notifyUser: boolean }) => {
  try {
    const client = await getCompanyApi();
    const response = await client.updateScoring(id, notifyUser);
    return {
      statusCode: response.status,
    };
  } catch (e) {
    const statusCode = get(e, ['response', 'status']);
    const errorCode = get(e, ['response', 'data', 'errorCode']);
    return {
      statusCode,
      errorCode,
    };
  }
};

// trigger a long process update score v2
export const Score = async ({ id, body }: { id: string; body: TriggerScoringByNameDto }) => {
  try {
    const client = await getCompanyApi();
    const response = await client.updateScoringV2(id, body);
    return {
      data: response.data,
      statusCode: response.status,
      errorCode: null,
    };
  } catch (e) {
    const statusCode = get(e, ['response', 'status']);
    const errorCode = get(e, ['response', 'data', 'errorCode']);
    return {
      data: null,
      statusCode: statusCode,
      errorCode: errorCode,
    };
  }
};

// trigger a long process update score on behalf of org, used by admin
export const UpdateScoringOnBehalfOfOrganisation = async ({
  companyId,
  organisationId,
}: {
  companyId: string;
  organisationId: string;
}) => {
  try {
    const client = await getCompanyApi();
    const response = await client.updateScoringOnBehalfOfOrganisation(companyId, organisationId);
    return {
      statusCode: response.status,
    };
  } catch (e) {
    const statusCode = get(e, ['response', 'status']);
    const errorCode = get(e, ['response', 'data', 'errorCode']);
    return {
      statusCode,
      errorCode,
    };
  }
};

export const GetCompany = async (companyId: string) => {
  try {
    const client = await getCompanyApi();
    const response = await client.byId(companyId);
    return response;
  } catch (e) {
    console.error(e);
    return null; // ignore fail reason
  }
};

export const PollCompanyScore = async (companyId: string) => {
  const client = await getCompanyApi();
  const response = await client.pollScoreById(companyId);
  return response;
};

export const ResetScoreMany = async (orgScoredCompanyReferenceIds: string[]) => {
  const client = await getCompanyApi();
  await client.resetScoreMany(orgScoredCompanyReferenceIds);
};

export const RescoreMany = async (orgScoredCompanyReferenceIds: string[]) => {
  const client = await getCompanyApi();
  await client.rescoreMany(orgScoredCompanyReferenceIds);
};

export const GetPaginatedAuditLogs = async (options: {
  pageIndex: number;
  fullTextSearch?: string;
  filters?: FilterItemDto[];
  sortColumn?: string;
  isDesc?: boolean;
}) => {
  const client = await getAuditLogApi();
  const response = await client.getPaginatedAuditLogs(options);
  return response.data || [];
};

export const GetAllAuditLogs = async (options: {
  filters?: FilterItemDto[];
  sortColumn?: string;
  isDesc?: boolean;
}) => {
  const client = await getAuditLogApi();
  const response = await client.getAll(options);
  return response.data || [];
};

export const GetAuditLogSummary = async (
  options: QueryAndSortDto,
): Promise<AuditLogSummaryDto[]> => {
  const client = await getAuditLogApi();
  const response = await client.getAuditLogSummary(options);
  return response.data || [];
};

export const GetExtractScoredCompanies = async (
  options: QueryAndSortDto,
): Promise<OrganisationScoredCompaniesDto[]> => {
  const client = await getOrganisationScoredCompaniesApi();
  const response = await client.extractScoredCompanies(options);
  return response.data || [];
};

export const GetExtractScoredSmartGovernanceCompanies = async (
  options: QueryAndSortDto,
): Promise<OrganisationScoredCompaniesDto[]> => {
  const client = await getOrganisationScoredCompaniesApi();
  const response = await client.extractScoredSmartGovernanceCompanies(options);
  return response.data || [];
};

export const GetScoredCompanies = async (options: {
  pageIndex: number;
  fullTextSearch?: string;
  filters?: FilterItemDto[];
  sortColumn?: string;
  isDesc?: boolean;
}) => {
  const client = await getKosmosCompanyApi();
  const response = await client.getScoredCompanies(options);
  return response.data || [];
};

export const GetScoredCompanyReports = async (options: {
  fullTextSearch?: string;
  filters?: FilterItemDto[];
  sortColumn?: string;
  isDesc?: boolean;
}): Promise<KosmosReportDto[]> => {
  const client = await getKosmosCompanyApi();
  const response = await client.getScoredCompanyReports(options);
  return response.data || [];
};

export const GetListScoredCompanies = async (options: {
  pageIndex: number;
  fullTextSearch?: string;
  filters?: FilterItemDto[];
  sortColumn?: string;
  isDesc?: boolean;
}) => {
  const client = await getOrganisationScoredCompaniesApi();
  const response = await client.listScoredCompanies(options);
  return response.data || [];
};

export const DisableOrganisation = async (orgId: string) => {
  const client = await getOrganisationsApi();
  await client.disableOrganisation(orgId);
};

export const EnableOrganisation = async (orgId: string) => {
  const client = await getOrganisationsApi();
  await client.enableOrganisation(orgId);
};

export const GetOrganisations = async (options: {
  pageIndex: number;
  fullTextSearch?: string;
  filters?: FilterItemDto[];
  sortColumn?: string;
  isDesc?: boolean;
}): Promise<OrganisationDtoQueryResultDto> => {
  const client = await getOrganisationsApi();
  const response = await client.getOrganisations(options);
  return response.data;
};

export const GetOrganisation = async (id: string): Promise<OrganisationDto> => {
  const client = await getOrganisationsApi();
  const response = await client.getOrganisation(id);
  return response.data;
};

export const UpdateOrganisation = async (
  id: string,
  attrs: UpdateOrganisationRequestDto,
): Promise<OrganisationDto> => {
  const client = await getOrganisationsApi();
  const res = await client.updateOrganisation(id, attrs);
  return res.data as OrganisationDto;
};

export const CreateOrganisation = async (requestBody: {
  name: string;
  displayName: string;
  hasUnlimitedSearches: boolean;
  hasUnlimitedCredits: boolean;
  isEvaluating: boolean;
  companyReportCsvFormat: string;
  billingType: string;
}): Promise<OrganisationDetailDto> => {
  const client = await getOrganisationsApi();
  const response = await client.createOrganisation(requestBody);
  return response.data;
};

export const AddCredits = async ({
  organisationId,
  numberOfCreditsToAdd,
}: {
  organisationId: string;
  numberOfCreditsToAdd: number;
}) => {
  const client = await getOrganisationsApi();
  const response = await client.addCredits(organisationId, numberOfCreditsToAdd);
  return response.data;
};

export const SetCreditsThreshold = async ({
  organisationId,
  thresholdLimit,
}: {
  organisationId: string;
  thresholdLimit: number;
}) => {
  const client = await getOrganisationsApi();
  const response = await client.setCreditsThreshold(organisationId, thresholdLimit);
  return response.data;
};
